import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaPlus } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import {
  getEnabledPackages,
  getAllowedTabNames,
  getAllowedTabIds,
  hasPackage,
  isWatconnectNavTab,
} from '../../utils/entitlements';
import { isHomeNavTab, NAV_TABS_UPDATED_EVENT } from '../../utils/navTabs';
import { useProductBuilderMode } from '../../utils/productBuilderMode';
import { theme } from '../../theme/theme';
import './css/Menu.css';

const titleCase = (str) =>
  (str || '').replace(/\w\S*/g, (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());

const MENU_ITEM_GAP = 8;
const MENU_MORE_BUTTON_WIDTH = 92;

const getBarItemLabel = (item) => {
  if (item.type === 'group') return item.label || '';
  return item.tab?.label || item.tab?.name || '';
};

const barItemHasCreateChevron = (item) =>
  item.type === 'tab' &&
  item.tab?.type === 'object' &&
  Boolean(item.tab?.object_name?.trim());

/** Rough px width for responsive overflow — keeps active tab visible when possible */
const estimateBarItemWidth = (item) => {
  const label = getBarItemLabel(item);
  const textWidth = Math.min(label.length * 7.5, 160);
  const padding = 32;
  const chevron = barItemHasCreateChevron(item) ? 28 : item.type === 'group' ? 22 : 0;
  return Math.max(52, textWidth + padding + chevron);
};

const computeMaxVisibleBarItems = (availableWidth, items) => {
  if (!items.length || availableWidth <= 0) return items.length;

  const widths = items.map(estimateBarItemWidth);

  for (let visible = items.length; visible >= 1; visible -= 1) {
    const visibleSum = widths
      .slice(0, visible)
      .reduce((sum, width) => sum + width, 0);
    const gaps = Math.max(0, visible - 1) * MENU_ITEM_GAP;
    const moreReserve = visible < items.length ? MENU_MORE_BUTTON_WIDTH + MENU_ITEM_GAP : 0;
    if (visibleSum + gaps + moreReserve <= availableWidth) {
      return visible;
    }
  }

  return 1;
};

/** Dev: always on in development. Prod: set localStorage spark_debug_tabs=1 */
const tabMenuDebugEnabled = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage?.getItem('spark_debug_tabs') === '1') {
      return true;
    }
  } catch (e) {
    /* ignore */
  }
  return process.env.NODE_ENV === 'development';
};

/**
 * Single place for tab list + bar items; exposes menuDebug when tabs disappear after login.
 */
function buildMenuTabComputation({
  activeTabs,
  selectedApp,
  enabledPackages,
  allowedTabIds,
  allowedTabNames,
  hasWatconnectPackage,
  productBuilderMode = false,
}) {
  const getGroupKey = (t) => (t.appGroupKey ?? t.config?.group_key ?? t.group_key)?.trim();
  const getGroupLabel = (t) => (t.appGroupLabel ?? t.config?.group_label ?? t.group_label)?.trim();
  const getTabPackage = (t) => (t.config?.package ?? t.package)?.trim?.() || '';

  const normalizeTabRef = (s) => (s || '').toString().toLowerCase().trim();
  const allowedIdSet = allowedTabIds.length > 0 ? new Set(allowedTabIds.map((id) => String(id))) : null;
  const allowedNameSet = allowedTabNames.length > 0 ? new Set(allowedTabNames) : null;
  const normalizeName = (s) => {
    const x = (s || '').toString().toLowerCase().trim();
    return x === 'calender' ? 'calendar' : x;
  };

  const hideUserMailTemplate = (t) => {
    const obj = normalizeTabRef(t?.object_name);
    const name = normalizeTabRef(t?.name);
    if (obj === 'user' || name === 'user') return false;
    if (obj === 'mail_config' || name === 'mail_config' || name === 'mailconfig') return false;
    if (obj === 'email_template' || name === 'email_template' || name === 'emailtemplate') return false;
    if (obj === 'email_layout' || name === 'email_layout' || name === 'emaillayout') return false;
    return true;
  };

  const trace = [];
  let unresolvedAppEntries = [];
  let visibleTabs;

  const hasAppTabs =
    selectedApp?.config &&
    (selectedApp.config.tabs?.length > 0 || selectedApp.config.tabIds?.length > 0);

  if (hasAppTabs) {
    const appTabsConfig = selectedApp.config.tabs?.length
      ? selectedApp.config.tabs
      : (selectedApp.config.tabIds || []).map((id) => ({ id }));
    const tabById = new Map(activeTabs.map((t) => [String(t.id), t]));
    const tabByName = new Map(activeTabs.map((t) => [(t?.name || '').toString().toLowerCase().trim(), t]));

    visibleTabs = appTabsConfig
      .map((entry) => {
        const tab =
          (entry.id != null ? tabById.get(String(entry.id)) : null) ||
          (entry.name != null
            ? tabByName.get((entry.name || '').toString().toLowerCase().trim())
            : null);
        if (!tab) {
          unresolvedAppEntries.push({
            entryId: entry.id,
            entryName: entry.name,
            entryGroup: entry.group_key,
          });
          return null;
        }
        return {
          ...tab,
          appGroupKey: entry.group_key?.trim() || undefined,
          appGroupLabel: entry.group_label?.trim() || undefined,
        };
      })
      .filter(Boolean);
    trace.push({
      step: 'after_app_config_merge',
      count: visibleTabs.length,
      unresolvedAppConfigEntries: unresolvedAppEntries.length,
    });
  } else if (productBuilderMode) {
    visibleTabs = [...activeTabs];
    trace.push({ step: 'after_product_builder_skip_package_filter', count: visibleTabs.length });
    visibleTabs = visibleTabs.filter(hideUserMailTemplate);
    trace.push({ step: 'after_default_hidden_tabs', count: visibleTabs.length });
  } else {
    visibleTabs = activeTabs.filter((t) => {
      const pkg = getTabPackage(t);
      return !pkg || enabledPackages.includes(pkg);
    });
    trace.push({ step: 'after_default_package_filter', count: visibleTabs.length });
    visibleTabs = visibleTabs.filter(hideUserMailTemplate);
    trace.push({ step: 'after_default_hidden_tabs', count: visibleTabs.length });
  }

  visibleTabs = visibleTabs.filter(hideUserMailTemplate);
  trace.push({ step: 'after_common_hidden_tabs', count: visibleTabs.length });

  if (hasAppTabs && !productBuilderMode) {
    const beforePkg = visibleTabs.length;
    visibleTabs = visibleTabs.filter((t) => {
      const pkg = getTabPackage(t);
      return !pkg || enabledPackages.includes(pkg);
    });
    trace.push({
      step: 'after_app_package_filter',
      count: visibleTabs.length,
      dropped: beforePkg - visibleTabs.length,
    });
  } else if (hasAppTabs && productBuilderMode) {
    trace.push({
      step: 'after_app_package_filter_skipped',
      note: 'product_builder mode',
      count: visibleTabs.length,
    });
  }

  let licenseDropDetail = null;
  const beforeLicense = visibleTabs;

  if (productBuilderMode) {
    trace.push({
      step: 'license_filter_skipped',
      note: 'product_builder mode — all tenant tabs visible',
      count: visibleTabs.length,
    });
  } else if (allowedIdSet && allowedIdSet.size > 0) {
    const rejects = beforeLicense.filter((t) => {
      if (t?.is_standard === false) return false;
      if (!t?.standard_tab_id) return true;
      return !allowedIdSet.has(String(t.standard_tab_id));
    });
    licenseDropDetail = {
      mode: 'allowedTabIds',
      allowedIdCount: allowedIdSet.size,
      rejectedCount: rejects.length,
      rejectedSample: rejects.slice(0, 20).map((t) => ({
        id: t.id,
        name: t.name,
        is_standard: t.is_standard,
        standard_tab_id: t.standard_tab_id,
        reason: !t?.standard_tab_id ? 'missing_standard_tab_id' : 'standard_tab_id_not_in_license',
      })),
    };
    visibleTabs = visibleTabs.filter((t) => {
      if (isHomeNavTab(t)) return true;
      // Custom tabs have no standard_tab_id; GET /api/tabs already applies role allowlist.
      if (t?.is_standard !== true) return true;
      return t?.standard_tab_id && allowedIdSet.has(String(t.standard_tab_id));
    });
  } else if (allowedNameSet && allowedNameSet.size > 0) {
    const rejects = beforeLicense.filter((t) => {
      if (t?.is_standard === false) return false;
      const name = normalizeName(t?.name);
      const label = normalizeName(t?.label);
      const obj = normalizeName(t?.object_name);
      const ok =
        (name && allowedNameSet.has(name)) ||
        (label && allowedNameSet.has(label)) ||
        (obj && allowedNameSet.has(obj));
      return !ok;
    });
    licenseDropDetail = {
      mode: 'allowedTabNames',
      allowedNameCount: allowedNameSet.size,
      rejectedCount: rejects.length,
      rejectedSample: rejects.slice(0, 20).map((t) => ({
        id: t.id,
        name: t.name,
        label: t.label,
        object_name: t.object_name,
        is_standard: t.is_standard,
      })),
    };
    visibleTabs = visibleTabs.filter((t) => {
      if (isHomeNavTab(t)) return true;
      if (t?.is_standard !== true) return true;
      const name = normalizeName(t?.name);
      const label = normalizeName(t?.label);
      const obj = normalizeName(t?.object_name);
      return (
        (name && allowedNameSet.has(name)) ||
        (label && allowedNameSet.has(label)) ||
        (obj && allowedNameSet.has(obj))
      );
    });
  } else {
    trace.push({
      step: 'license_filter_skipped',
      note: 'No allowedTabIds / allowedTabNames in session entitlements',
    });
  }
  trace.push({ step: 'after_license_filter', count: visibleTabs.length, licenseDropDetail });

  const beforeWatconnect = visibleTabs.length;
  if (!productBuilderMode && !hasWatconnectPackage) {
    visibleTabs = visibleTabs.filter((t) => !isWatconnectNavTab(t));
  }
  trace.push({
    step: 'after_watconnect_filter',
    count: visibleTabs.length,
    droppedWatconnectOnlyTabs: hasWatconnectPackage ? 0 : beforeWatconnect - visibleTabs.length,
  });

  // Preserve GET /api/tabs array order (includes per-user tab preferences).
  const tabOrderIndex = new Map(
    visibleTabs.map((t, i) => [String(t.id), i])
  );
  const compareByNavOrder = (a, b) =>
    (tabOrderIndex.get(String(a.id)) ?? 9999) - (tabOrderIndex.get(String(b.id)) ?? 9999);

  const slotOrder = selectedApp?.config?.slotOrder;
  let barItems;
  if (Array.isArray(slotOrder) && slotOrder.length > 0 && selectedApp?.config?.tabs?.length > 0) {
    barItems = [];
    slotOrder.forEach((slot) => {
      if (slot?.type === 'tab') {
        const tab = visibleTabs.find(
          (t) =>
            (slot.name != null &&
              (t?.name || '').toString().toLowerCase().trim() ===
              (slot.name || '').toString().toLowerCase().trim()) ||
            (slot.id != null && String(t?.id) === String(slot.id))
        );
        if (tab) barItems.push({ type: 'tab', tab });
      } else if (slot?.type === 'group' && slot.group_key) {
        const gk = (slot.group_key || '').trim();
        const groupTabs = visibleTabs
          .filter((t) => getGroupKey(t) === gk)
          .sort(compareByNavOrder);
        if (groupTabs.length > 0) {
          const label = getGroupLabel(groupTabs[0]) || titleCase(gk);
          barItems.push({ type: 'group', id: `api-${gk}`, key: gk, label, tabs: groupTabs });
        }
      }
    });
  } else {
    barItems = [];
    const groupIdsAdded = new Set();
    visibleTabs.forEach((tab) => {
      const gk = getGroupKey(tab);
      if (!gk) {
        barItems.push({ type: 'tab', tab });
        return;
      }
      if (groupIdsAdded.has(gk)) return;
      groupIdsAdded.add(gk);
      const groupTabs = visibleTabs
        .filter((t) => getGroupKey(t) === gk)
        .sort(compareByNavOrder);
      const label = getGroupLabel(groupTabs[0]) || titleCase(gk);
      barItems.push({ type: 'group', id: `api-${gk}`, key: gk, label, tabs: groupTabs });
    });
  }

  const slotOrderMismatch =
    Array.isArray(slotOrder) &&
    slotOrder.length > 0 &&
    selectedApp?.config?.tabs?.length > 0 &&
    visibleTabs.length > 0 &&
    barItems.length === 0;

  return {
    visibleTabs,
    barItems,
    menuDebug: {
      trace,
      licenseDropDetail,
      unresolvedAppEntries,
      hasAppTabs,
      selectedAppId: selectedApp?.id ?? null,
      selectedAppLabel: selectedApp?.name ?? selectedApp?.app_name ?? null,
      apiTabCount: activeTabs.length,
      slotOrderLength: Array.isArray(slotOrder) ? slotOrder.length : 0,
      slotOrderMismatch,
      enabledPackages,
      allowedTabIdsCount: allowedTabIds.length,
      allowedTabNamesCount: allowedTabNames.length,
    },
  };
}

const Menu = ({ selectedAppId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const productBuilderMode = useProductBuilderMode();
  const [activeTabs, setActiveTabs] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loadingTabs, setLoadingTabs] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState(() => new Set());
  const menuRootRef = useRef(null);
  const [maxVisible, setMaxVisible] = useState(10);

  useEffect(() => {
    fetchActiveTabs();
  }, []);

  useEffect(() => {
    const onTabsUpdated = () => {
      fetchActiveTabs();
    };
    window.addEventListener(NAV_TABS_UPDATED_EVENT, onTabsUpdated);
    return () => window.removeEventListener(NAV_TABS_UPDATED_EVENT, onTabsUpdated);
  }, []);

  useEffect(() => {
    if (!selectedAppId) {
      setSelectedApp(null);
      return;
    }
    const fetchApp = async () => {
      try {
        const appsApi = new DataApi('apps');
        const res = await appsApi.fetchById(selectedAppId);
        if (res?.data?.success && res.data.data) setSelectedApp(res.data.data);
        else setSelectedApp(null);
      } catch (err) {
        console.error('Error fetching selected app for menu:', err);
        setSelectedApp(null);
      }
    };
    fetchApp();
  }, [selectedAppId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (openDropdownId !== null) {
      const handleClickOutside = (event) => {
        // Check if click is outside the dropdown
        const dropdownElement = document.querySelector(`[data-tab-dropdown-id="${openDropdownId}"]`);
        if (dropdownElement && !dropdownElement.contains(event.target)) {
          setOpenDropdownId(null);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdownId]);

  const fetchActiveTabs = async () => {
    try {
      setLoadingTabs(true);
      const tabApi = new DataApi('tabs');
      // Fetch tabs for web platform (use 'platform=mobile' for mobile app)
      const response = await tabApi.fetchAll('is_active=true&platform=web');
      if (tabMenuDebugEnabled() && response?.data?.success) {
        console.log('[Menu/tab-debug] GET /tabs count:', response.data.data?.length);
      }
      if (response?.data?.success) {
        // Use server order (includes per-user preferences when feature is enabled)
        setActiveTabs(response.data.data || []);
      } else {
        setActiveTabs([]);
      }
    } catch (error) {
      console.error('Error fetching active tabs:', error);
      setActiveTabs([]);
    } finally {
      setLoadingTabs(false);
    }
  };

  const normalizePath = path => {
    if (!path) {
      return '';
    }
    if (path === '/') {
      return '/';
    }
    const trimmed = path.replace(/\/+$/, '');
    return trimmed.toLowerCase();
  };

  const currentPath = normalizePath(location.pathname);

  const doesPathMatch = basePath => {
    if (!basePath) return false;
    if (currentPath === basePath) {
      return true;
    }
    return currentPath.startsWith(`${basePath}/`);
  };

  const isTabActive = tab => {
    if (!tab) return false;

    const tabUrl = normalizePath(tab.url);
    if (tabUrl && doesPathMatch(tabUrl)) {
      return true;
    }

    const objectName = tab.object_name?.toLowerCase();
    if (objectName) {
      const objectPaths = ['/listview', '/view', '/edit'].map(
        prefix => `${prefix}/${objectName}`
      );

      if (objectPaths.some(path => doesPathMatch(path))) {
        return true;
      }
    }

    return false;
  };

  const menuResult = useMemo(
    () =>
      buildMenuTabComputation({
        activeTabs,
        selectedApp,
        enabledPackages: getEnabledPackages(),
        allowedTabIds: getAllowedTabIds(),
        allowedTabNames: getAllowedTabNames(),
        hasWatconnectPackage: hasPackage('watconnect'),
        productBuilderMode,
      }),
    [activeTabs, selectedApp, productBuilderMode]
  );

  const { visibleTabs, barItems, menuDebug } = menuResult;

  useEffect(() => {
    const root = menuRootRef.current;
    if (!root || loadingTabs) return;

    const updateVisibleCount = () => {
      const width = root.clientWidth;
      setMaxVisible(computeMaxVisibleBarItems(width, barItems));
    };

    updateVisibleCount();

    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(root);
    window.addEventListener('resize', updateVisibleCount);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateVisibleCount);
    };
  }, [barItems, loadingTabs]);

  const tabDebugLastKeyRef = useRef('');
  useEffect(() => {
    if (!tabMenuDebugEnabled()) return;
    if (loadingTabs) return;

    if (activeTabs.length === 0) {
      const key = 'api-empty';
      if (tabDebugLastKeyRef.current !== key) {
        tabDebugLastKeyRef.current = key;
        console.warn('[Menu/tab-debug] No rows from GET /tabs after load.');
      }
      return;
    }

    if (visibleTabs.length > 0 && !menuDebug.slotOrderMismatch) return;

    const key = `${menuDebug.apiTabCount}:${String(menuDebug.selectedAppId)}:${visibleTabs.length}:${barItems.length}:${menuDebug.slotOrderMismatch}`;
    if (tabDebugLastKeyRef.current === key) return;
    tabDebugLastKeyRef.current = key;

    if (visibleTabs.length === 0) {
      console.warn('[Menu/tab-debug] All tabs filtered out (see trace + licenseDropDetail).', menuDebug);
    }
    if (menuDebug.slotOrderMismatch) {
      console.warn(
        '[Menu/tab-debug] visibleTabs > 0 but slotOrder built an empty bar — check slot id/name vs tenant tab rows.',
        { visibleTabCount: visibleTabs.length, barItemCount: barItems.length, menuDebug }
      );
    }
  }, [loadingTabs, activeTabs.length, visibleTabs.length, barItems.length, menuDebug]);

  if (loadingTabs) {
    return (
      <div className='d-flex align-items-center'>
        <div
          className='spinner-border spinner-border-sm text-light me-2'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
        <span className='text-light'>Loading...</span>
      </div>
    );
  }

  // Split bar items into visible tabs and "More" dropdown based on available width
  const visibleBarItems = barItems.slice(0, maxVisible);
  const moreBarItems = barItems.slice(maxVisible);
  const routeActiveTab = visibleTabs.find((tab) => isTabActive(tab));
  const activeInVisible = routeActiveTab && visibleBarItems.some((item) =>
    item.type === 'tab' ? item.tab.id === routeActiveTab.id : item.tabs.some((t) => t.id === routeActiveTab.id)
  );
  const activeBarItem = routeActiveTab && barItems.find((item) =>
    item.type === 'tab' ? item.tab.id === routeActiveTab.id : item.tabs.some((t) => t.id === routeActiveTab.id)
  );
  const supplementalBarItems = !activeInVisible && activeBarItem ? [activeBarItem] : [];
  const orderedVisibleBarItems =
    supplementalBarItems.length > 0
      ? [...visibleBarItems.filter((item) => item !== activeBarItem), ...supplementalBarItems]
      : visibleBarItems;
  const dropdownBarItems = moreBarItems.filter((item) => item !== activeBarItem);

  const showNewRecordDropdown = (tab) =>
    tab?.type === 'object' && Boolean(tab?.object_name?.trim());

  const handleCreateRecord = tab => {
    const objectName = tab.object_name?.toLowerCase();
    if (!objectName) return;
    navigate(`/edit/${objectName}`);
  };

  return (
    <div ref={menuRootRef} className="menu-bar-root">
      {/* Render bar: top-level tabs and group dropdowns in one order (by order_no) */}
      {orderedVisibleBarItems.map((barItem) => {
        if (barItem.type === 'group') {
          const group = barItem;
          const groupActive = group.tabs.some((tab) => isTabActive(tab));
          return (
            <Dropdown key={group.id} drop="down" popperConfig={{ strategy: 'fixed' }}>
              <Dropdown.Toggle
                variant="link"

                className="menu-group-toggle menu-bar-item"
                style={{
                  color: groupActive ? '#0d47a1' : '#1a1a1a',
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  margin: '0',
                  padding: '10px 16px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: groupActive ? '600' : '500',
                  borderBottom: groupActive ? '3px solid #0d47a1' : '3px solid transparent',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#0d47a1';
                  e.currentTarget.style.borderBottom = '3px solid #bbdefb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = groupActive ? '#0d47a1' : '#1a1a1a';
                  e.currentTarget.style.borderBottom = groupActive ? '3px solid #0d47a1' : '3px solid transparent';
                }}
              >
                <span className="menu-bar-item-label" title={group.label}>{group.label}</span>{' '}
                <FaChevronDown size={10} style={{ marginLeft: '4px', flexShrink: 0 }} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                align="end"
                popperConfig={{ strategy: 'fixed' }}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: '200px',
                  padding: '8px 0',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1060,
                }}
              >
                {group.tabs.map((tab) => (
                  <Dropdown.Item
                    key={tab.id}
                    as={NavLink}
                    to={tab.url || '#'}
                    style={{
                      color: isTabActive(tab) ? theme.colors.primary : '#333',
                      fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                      padding: '8px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      backgroundColor: isTabActive(tab) ? '#f8f9fa' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = theme.colors.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = isTabActive(tab) ? '#f8f9fa' : 'transparent';
                      e.target.style.color = isTabActive(tab) ? theme.colors.primary : '#333';
                    }}
                  >
                    {tab.label || tab.name?.replace(new RegExp(group.key, 'gi'), '').replace(/_/g, ' ').trim() || tab.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          );
        }
        const tab = barItem.tab;
        const renderTabLink = () => (
          <NavLink
            to={tab.url || '#'}
            className='nav-link menu-nav-link menu-bar-item'
            title={tab.label}
            style={({ isActive }) => {
              const active = isActive || isTabActive(tab);
              return {
                color: active ? '#0d47a1' : '#1a1a1a',
                fontWeight: active ? '600' : '500',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 0,
                margin: '0',
                padding: '10px 16px',
                transition: 'all 0.2s ease',
                borderBottom: active
                  ? '3px solid #0d47a1'
                  : '3px solid transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              };
            }}
            onMouseEnter={e => {
              if (!isTabActive(tab)) {
                e.currentTarget.style.color = '#0d47a1';
                e.currentTarget.style.borderBottom = '3px solid #bbdefb';
              }
            }}
            onMouseLeave={e => {
              const active = isTabActive(tab);
              e.currentTarget.style.color = active ? '#0d47a1' : '#1a1a1a';
              e.currentTarget.style.borderBottom = active
                ? '3px solid #0d47a1'
                : '3px solid transparent';
            }}
          >
            {tab.label}
          </NavLink>
        );

        if (!showNewRecordDropdown(tab)) {
          return (
            <div
              key={tab.id}
              className="menu-bar-tab-wrap"
            >
              {renderTabLink()}
            </div>
          );
        }

        return (
          <div
            key={tab.id}
            data-tab-dropdown-id={tab.id}
            className="menu-bar-tab-wrap"
            style={{ position: 'relative' }}
          >
            <div onClick={(e) => e.stopPropagation()}>
              {renderTabLink()}
            </div>
            <Dropdown
              align='end'
              drop="down"
              popperConfig={{ strategy: 'fixed' }}
              show={openDropdownId === tab.id}
              onToggle={(isOpen) => {
                if (isOpen) {
                  setOpenDropdownId(tab.id);
                } else {
                  setOpenDropdownId(null);
                }
              }}
            >
              <Dropdown.Toggle
                variant='link'
                size='sm'
                className='tab-dropdown-toggle'
                style={{
                  color: '#1a1a1a',
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '10px 6px',
                  marginLeft: '-8px',
                  borderBottom: '3px solid transparent',
                }}
                onClick={(e) => {
                  // Stop propagation to prevent tab navigation when clicking chevron
                  e.stopPropagation();
                  e.preventDefault();
                  setOpenDropdownId(openDropdownId === tab.id ? null : tab.id);
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = theme.colors.primary;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#1a1a1a';
                }}
              >
                <FaChevronDown size={10} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                popperConfig={{ strategy: 'fixed' }}
                style={{
                  minWidth: 'auto',
                  width: 'max-content',
                  zIndex: 1060,
                  padding: '4px 0',
                }}
              >
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(null);
                    handleCreateRecord(tab);
                  }}
                  className='d-flex align-items-center'
                  style={{
                    padding: '6px 12px',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FaPlus size={12} style={{ flexShrink: 0 }} />
                  <span>New {tab.label || tab.object_name}</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      })}

      {/* More: remaining bar items (tabs and groups) */}
      {dropdownBarItems.length > 0 && (
        <Dropdown className="menu-more-dropdown" drop="down" popperConfig={{ strategy: 'fixed' }}>
          <Dropdown.Toggle
            className="no-caret menu-more-toggle menu-bar-item"
            variant='link'
            style={{
              color: '#1a1a1a',
              border: 'none',
              backgroundColor: 'transparent',
              borderRadius: 0,
              margin: '0',
              padding: '10px 16px',
              transition: 'all 0.2s ease',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: '3px solid transparent',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
            onMouseEnter={e => {
              e.target.style.color = '#0d47a1';
              e.target.style.borderBottom = '3px solid #bbdefb';
            }}
            onMouseLeave={e => {
              e.target.style.color = '#1a1a1a';
              e.target.style.borderBottom = '3px solid transparent';
            }}
          >
            More ({dropdownBarItems.length}) <FaChevronDown size={10} style={{ marginLeft: '4px' }} />
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="menu-more-dropdown-menu"
            align='end'
            popperConfig={{ strategy: 'fixed' }}
            style={{
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: '200px',
              padding: '8px 0',
              maxHeight: '400px',
              overflowY: 'auto',
              overflowX: 'visible',
              zIndex: 1060,
            }}
          >
            {dropdownBarItems.map((barItem) => {
              if (barItem.type === 'tab') {
                const tab = barItem.tab;
                return (
                  <Dropdown.Item
                    key={tab.id}
                    as={NavLink}
                    to={tab.url || '#'}
                    style={{
                      color: isTabActive(tab) ? theme.colors.primary : '#333',
                      fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                      padding: '8px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      backgroundColor: isTabActive(tab) ? '#f8f9fa' : 'transparent',
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = '#f8f9fa'; e.target.style.color = theme.colors.primary; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = isTabActive(tab) ? '#f8f9fa' : 'transparent'; e.target.style.color = isTabActive(tab) ? theme.colors.primary : '#333'; }}
                  >
                    {tab.label}
                  </Dropdown.Item>
                );
              }
              const group = barItem;
              const isExpanded = expandedGroupKeys.has(group.key);
              const groupActive = group.tabs.some((tab) => isTabActive(tab));

              return (
                <div key={`group-${group.key}`} className="menu-more-group-wrap">
                  <div
                    className="menu-more-group-item"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setExpandedGroupKeys((prev) => {
                        const next = new Set(prev);
                        if (next.has(group.key)) {
                          next.delete(group.key);
                        } else {
                          next.add(group.key);
                        }
                        return next;
                      });
                    }}
                    onMouseEnter={() => {
                      setExpandedGroupKeys((prev) => {
                        if (prev.has(group.key)) return prev;
                        const next = new Set(prev);
                        next.add(group.key);
                        return next;
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: groupActive ? 600 : 500,
                      cursor: 'pointer',
                      backgroundColor: groupActive ? '#f0f4f8' : 'transparent',
                      color: groupActive ? theme.colors.primary : '#333',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{group.label}</span>
                    {isExpanded ? (
                      <FaChevronDown size={10} className="ms-2" style={{ flexShrink: 0 }} />
                    ) : (
                      <FaChevronRight size={10} className="ms-2" style={{ flexShrink: 0 }} />
                    )}
                  </div>

                  {isExpanded && (
                    <div
                      className="menu-more-group-children"
                      style={{
                        backgroundColor: '#fafafa',
                        borderLeft: '2px solid #0d47a1',
                        marginLeft: '12px',
                        marginRight: '8px',
                        borderRadius: '0 4px 4px 0',
                        padding: '2px 0',
                      }}
                    >
                      {group.tabs.map((tab) => (
                        <Dropdown.Item
                          key={tab.id}
                          as={NavLink}
                          to={tab.url || '#'}
                          style={{
                            display: 'block',
                            padding: '6px 14px',
                            fontSize: '13.5px',
                            color: isTabActive(tab) ? theme.colors.primary : '#444',
                            fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                            textDecoration: 'none',
                            backgroundColor: isTabActive(tab) ? '#eef4ff' : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#eef4ff';
                            e.currentTarget.style.color = theme.colors.primary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isTabActive(tab) ? '#eef4ff' : 'transparent';
                            e.currentTarget.style.color = isTabActive(tab) ? theme.colors.primary : '#444';
                          }}
                        >
                          {tab.label || tab.name?.replace(new RegExp(group.key, 'gi'), '').replace(/_/g, ' ').trim() || tab.name}
                        </Dropdown.Item>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
};

export default Menu;
